import { Component,
  ElementRef,
  HostListener,
  Input,
  QueryList,
  ViewChild,
  ViewChildren, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { CommonModule } from '@angular/common';
import { FORM_ID } from '../../../helpers/form-id.enum';
import { CommonService } from '../../../api/services/common.service';
import { AuthService } from '../../../api/services/auth.service';
import { getAccessValue } from '../../../helpers/rbac.helper';
import { NoDataViewComponent } from '../no-data-view/no-data-view.component';
import { ModalRendererComponent } from '../../widgets/modal-renderer/modal-renderer.component';

@Component({
    selector: 'app-card-type-ten',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        NoDataViewComponent,
        ModalRendererComponent,
    ],
    templateUrl: './card-type-ten.component.html',
    styleUrl: './card-type-ten.component.css'
})
export class CardTypeTenComponent {
  endpoint = environment.api_endpoint;
  @Input() fields: any;
  @Input() formGroup!: FormGroup;
  @Input() activeTab: any;
  @Input() isWriteAccess: boolean = true;
  isLoading: boolean = false;
  fieldsData: any;
  formID = FORM_ID.BU_LIST;
  searchInput: any;
  sortValue: any;
  editID: any;
  @ViewChild(ModalRendererComponent) modalRendererRef!: ModalRendererComponent;
  modalTitle: any;
  activeModalContext: any;
  profileResponse: any;
  openAboveId: string | null = null;
  openDropdownId: string | null = null;
  @ViewChildren('dropdownButton') dropdownButtons!: QueryList<ElementRef>;
  constructor(
    private commonService: CommonService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
  ) {}
  ngOnInit() {
    this.loadData(this.formID, {
      sort: '-createdAt',
    });
    this.getProfileDetails();
  }

  getProfileDetails() {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    this.authService.getProfile(token).subscribe({
      next: (data) => {
        this.profileResponse = data?.data;
        const accessVal = getAccessValue(
          this.profileResponse,
          'settings-module',
          'organization',
        );
        if (accessVal === 'read_only' || accessVal === 'no_access') {
          this.isWriteAccess = false;
        }
        this.cdr.detectChanges();
      },
      error: () => {},
    });
  }

  /** Function to get the data by api or static */
  loadData(formId: any, options: { [key: string]: any } = {}) {
    this.isLoading = true;

    this.commonService.loadListWithOptions(formId, options).subscribe({
      next: (data) => {
        this.fieldsData = data.data.records;
        this.isLoading = false;
       this.cdr.detectChanges();},
      error: () => {
        this.fieldsData = [];
        this.isLoading = false;
       this.cdr.detectChanges();},
    });
  }

  /** Function to set the modal title */
  setModalTitle(title: any) {
    this.modalTitle = !this.isWriteAccess ? 'View Business Unit' : title;
  }

  openEditModal(item: any) {
    this.modalRendererRef?.resetForms();
    this.editID = item._id;
    let formID = FORM_ID.BU_EDIT;

    if (formID) {
      this.activeModalContext = {
        nodeId: formID,
        recordID: item._id,
      };
    }
    setTimeout(() => {
      const modalId = `open_modal_${item._id}`;
      const modal = document.getElementById(modalId);
      if (modal) modal.classList.add('show');
    }, 0);
  }

  closeOnBackdropClick(event: MouseEvent, modalId: any) {
    const modal = document.getElementById(modalId);
    if (event.target === modal) {
      this.closeModal(modalId);
    }
  }

  closeModal(modalID: any) {
    const modal = document.getElementById(modalID);
    if (modal) modal.classList.remove('show');
    this.activeModalContext = null;
  }

  openCurrentModal() {
    const modalId = `open_modal_${this.editID}`;
    const modal = document.getElementById(modalId);
    if (modal) modal.classList.add('show');
  }

  /**
   * Reloads the list and closes the modal after a successful save.
   */
  onModalFormSaved(event: any) {
    this.loadData(this.formID, {
      query: this.searchInput,
      sort: this.sortValue || '-createdAt',
    });
    const modalId = `open_modal_${this.editID}`;
    this.closeModal(modalId);
    setTimeout(() => {
      this.modalRendererRef?.resetForms();
    }, 100);
    this.editID = null;
    this.activeModalContext = null;
  }

  closeAllDropdowns() {
    this.openDropdownId = null;
    this.openAboveId = null;
  }

  /**** Toggle dropdown */
  toggleDropdown(id: string, event: MouseEvent) {
    event.stopPropagation();
    if (this.openDropdownId === id) {
      this.closeAllDropdowns();
      return;
    }
    this.openDropdownId = id;
    this.calculateDropdownPosition(id);
  }

  /**** Check if dropdown is open */
  isDropdownOpen(id: string): boolean {
    return this.openDropdownId === id;
  }

  /****  Close dropdown when clicking anywhere outside */
  @HostListener('document:click')
  onOutsideClick() {
    this.closeAllDropdowns();
  }

  /**
   * Determines whether a dropdown should open above or below based on viewport space.
   */
  calculateDropdownPosition(id: string) {
    const button = this.dropdownButtons.find(
      (btn) => btn.nativeElement.dataset.id === id,
    );
    if (button) {
      const rect = button.nativeElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const dropdownHeight = 200;

      if (spaceBelow < dropdownHeight) {
        this.openAboveId = id;
      } else {
        this.openAboveId = null;
      }
    }
  }

  /**
   * Opens the delete modal for the selected bookmark.
   */
  deleteBusinessUnit(item: any) {
    this.editID = item._id;
    let formID = FORM_ID.BU_DELETE;

    if (formID) {
      this.activeModalContext = {
        nodeId: formID,
        recordID: item._id,
        businessUnitName: item.name,
      };
      setTimeout(() => {
        const modalId = `open_modal_${item._id}`;
        const modal = document.getElementById(modalId);
        if (modal) modal.classList.add('show');
      }, 0);
    }
  }

  shouldOpenAbove(id: string): boolean {
    return this.openAboveId === id;
  }

  getLocationNames(location: any): string {
    if (Array.isArray(location)) {
      const names = location.map((loc) => loc.name);

      if (names.length > 3) {
        const visible = names.slice(0, 3).join(', ');
        const remaining = names.length - 3;
        return `${visible} +${remaining} more`;
      }

      return names.join(', ');
    }

    return location?.name || '';
  }

  getFullLocationNames(location: any): string {
    if (Array.isArray(location)) {
      return location.map((loc) => loc.name).join(', ');
    }
    return location?.name || '';
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    const modalId = `open_modal_${this.editID}`;
    this.closeModal(modalId);
  }
}
